import { FC, useCallback, useMemo, useState } from 'react';
import {
  Button,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from '@carbon/react';
import {
  Add,
  CheckmarkOutline,
  Edit,
  Flag,
  TrashCan,
  Undo,
  UserFollow,
} from '@carbon/react/icons';
import { useSetAtom } from 'jotai';
import {
  clearAddressFlagAtom,
  clearAddressFlagUrl,
  createDocumentAddressAtom,
  deleteDocumentAddressAtom,
  documentAddressesBaseUrl,
  documentAddressUrl,
  markAddressCorrectAtom,
  markAddressCorrectUrl,
  resolveAddressToPartyAtom,
  resolveToPartyUrl,
  restoreAddressAtom,
  restoreAddressUrl,
  updateDocumentAddressAtom,
} from './atoms/structured-addresses';
import { AddressReviewFlag } from './address-review-flag';
import { IconAction } from './blocked-action';
import { AddDocumentAddressPanel } from './add-document-address-panel';
import { ResolveToPartyPanel } from './resolve-to-party-panel';
import { correctStatusLockReason, STATUS_CELL_CONFIG } from './party-status';
import type { DocumentAddress, StructuredPartyListItem } from './types';
import style from './document-addresses-section.module.scss';

interface Props {
  documentId: number;
  addresses: DocumentAddress[];
  parties: StructuredPartyListItem[];
  onChange: () => void;
  embedded?: boolean;
  addPanelOpen?: boolean;
  onAddPanelOpenChange?: (open: boolean) => void;
}

const addressSearchText = (a: DocumentAddress): string =>
  [a.fullText, a.asFoundAddress, a.streetAddress, a.city, a.state, a.zipCode]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

export const DocumentAddressesSection: FC<Props> = ({
  documentId,
  addresses,
  parties,
  onChange,
  embedded = false,
  addPanelOpen,
  onAddPanelOpenChange,
}) => {
  const [internalAddOpen, setInternalAddOpen] = useState(false);
  const addOpen = addPanelOpen !== undefined ? addPanelOpen : internalAddOpen;
  const setAddOpen = (open: boolean) => {
    if (onAddPanelOpenChange) onAddPanelOpenChange(open);
    else setInternalAddOpen(open);
  };
  const [resolveTarget, setResolveTarget] = useState<DocumentAddress | null>(
    null
  );
  const [editTarget, setEditTarget] = useState<DocumentAddress | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const triggerClearFlag = useSetAtom(clearAddressFlagAtom);
  const triggerMarkCorrect = useSetAtom(markAddressCorrectAtom);
  const triggerRemove = useSetAtom(deleteDocumentAddressAtom);
  const triggerRestore = useSetAtom(restoreAddressAtom);
  const triggerResolve = useSetAtom(resolveAddressToPartyAtom);
  const triggerCreate = useSetAtom(createDocumentAddressAtom);
  const triggerUpdate = useSetAtom(updateDocumentAddressAtom);

  const handleClearFlag = useCallback(
    (address: DocumentAddress) => {
      setActionError(null);
      triggerClearFlag({
        url: clearAddressFlagUrl(documentId, address.id),
        type: 'POST',
        data: null,
        onSuccess: () => onChange(),
        onError: (err) => setActionError(err?.error ?? 'Could not clear flag.'),
      });
    },
    [documentId, onChange, triggerClearFlag]
  );

  const handleMarkCorrect = useCallback(
    (address: DocumentAddress) => {
      setActionError(null);
      triggerMarkCorrect({
        url: markAddressCorrectUrl(documentId, address.id),
        type: 'POST',
        data: null,
        onSuccess: () => onChange(),
        onError: (err) =>
          setActionError(err?.error ?? 'Could not update correct status.'),
      });
    },
    [documentId, onChange, triggerMarkCorrect]
  );

  const handleRemove = useCallback(
    (address: DocumentAddress) => {
      setActionError(null);
      triggerRemove({
        url: documentAddressUrl(documentId, address.id),
        type: 'DELETE',
        data: null,
        onSuccess: () => onChange(),
        onError: (err) =>
          setActionError(err?.error ?? 'Could not remove address.'),
      });
    },
    [documentId, onChange, triggerRemove]
  );

  const handleRestore = useCallback(
    (address: DocumentAddress) => {
      setActionError(null);
      triggerRestore({
        url: restoreAddressUrl(documentId, address.id),
        type: 'POST',
        data: null,
        onSuccess: () => onChange(),
        onError: (err) =>
          setActionError(err?.error ?? 'Could not restore address.'),
      });
    },
    [documentId, onChange, triggerRestore]
  );

  // The Add/Resolve/Edit panels await the submit prop to drive their own
  // submitting indicator. Wrap the mutation-atom trigger in a Promise so we
  // can keep the same prop signature.
  const submitResolve = useCallback(
    (partyIds: number[]) =>
      new Promise<void>((resolvePromise) => {
        if (!resolveTarget) return resolvePromise();
        setActionError(null);
        triggerResolve({
          url: resolveToPartyUrl(documentId, resolveTarget.id),
          type: 'POST',
          data: { party_ids: partyIds },
          onSuccess: () => {
            setResolveTarget(null);
            onChange();
            resolvePromise();
          },
          onError: (err) => {
            setActionError(err?.error ?? 'Could not resolve address to party.');
            resolvePromise();
          },
        });
      }),
    [documentId, onChange, resolveTarget, triggerResolve]
  );

  const submitCreate = useCallback(
    (attrs: Partial<DocumentAddress>) =>
      new Promise<void>((resolvePromise) => {
        setActionError(null);
        triggerCreate({
          url: documentAddressesBaseUrl(documentId),
          type: 'POST',
          data: attrs as never,
          onSuccess: () => {
            setAddOpen(false);
            onChange();
            resolvePromise();
          },
          onError: (err) => {
            setActionError(err?.error ?? 'Could not add address.');
            resolvePromise();
          },
        });
      }),
    // setAddOpen is a stable wrapper around either internal state or the
    // controlled prop's setter; safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentId, onChange, triggerCreate]
  );

  const submitEdit = useCallback(
    (attrs: Partial<DocumentAddress>) =>
      new Promise<void>((resolvePromise) => {
        if (!editTarget) return resolvePromise();
        setActionError(null);
        triggerUpdate({
          url: documentAddressUrl(documentId, editTarget.id),
          type: 'PATCH',
          data: attrs as never,
          onSuccess: () => {
            setEditTarget(null);
            onChange();
            resolvePromise();
          },
          onError: (err) => {
            setActionError(err?.error ?? 'Could not update address.');
            resolvePromise();
          },
        });
      }),
    [documentId, onChange, editTarget, triggerUpdate]
  );

  const filteredAddresses = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    return search
      ? addresses.filter((a) => addressSearchText(a).includes(search))
      : addresses;
  }, [addresses, searchValue]);

  return (
    <section className={style.section}>
      {!embedded && (
        <div className={style.header}>
          <span className={style.title}>
            Non-Party Addresses
            <span className={style.titleCount}>{addresses.length}</span>
          </span>
        </div>
      )}

      {actionError && (
        <div className={style.notification}>
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={actionError}
            onCloseButtonClick={() => setActionError(null)}
          />
        </div>
      )}

      {addresses.length === 0 ? (
        <div className={style.empty}>
          No non-party addresses captured on this document.
        </div>
      ) : (
        <TableContainer>
          <TableToolbar size="sm">
            <TableToolbarContent>
              <TableToolbarSearch
                placeholder="Search non-party addresses…"
                persistent
                size="sm"
                value={searchValue}
                onChange={(e) =>
                  setSearchValue(
                    (e as React.ChangeEvent<HTMLInputElement>).target?.value ??
                      ''
                  )
                }
              />
              <Button
                kind="primary"
                size="sm"
                renderIcon={Add}
                onClick={() => setAddOpen(true)}
              >
                Add Non-Party Address
              </Button>
            </TableToolbarContent>
          </TableToolbar>
          <Table size="md">
            <TableHead>
              <TableRow>
                <TableHeader className={style.colNo}>No.</TableHeader>
                <TableHeader className={style.colStatus}>Status</TableHeader>
                <TableHeader>Address</TableHeader>
                <TableHeader className={style.colActions}>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAddresses.map((address) => {
                const originalIndex = addresses.indexOf(address);
                const isRemoved =
                  address.removed || address.status === 'removed';
                const isComplete = address.status === 'complete';
                const cfg = STATUS_CELL_CONFIG[address.status];
                const StatusIcon = cfg.icon;
                return (
                  <TableRow
                    key={address.id}
                    className={isRemoved ? style.rowRemoved : undefined}
                  >
                    <TableCell>{isRemoved ? '' : originalIndex + 1}</TableCell>
                    <TableCell>
                      <span
                        className={`${style.statusCell} ${
                          style[cfg.className]
                        }`}
                      >
                        <StatusIcon />
                        {cfg.label}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className={style.flagCell}>
                        <span
                          className={
                            !isRemoved && address.reviewFlag
                              ? `${style.confidenceHighlight} ${
                                  address.reviewFlag.criticality === 'high'
                                    ? style.confidenceHighlightHigh
                                    : style.confidenceHighlightMed
                                }`
                              : undefined
                          }
                        >
                          {address.fullText || address.asFoundAddress}
                        </span>
                        {!isRemoved && address.reviewFlag && (
                          <AddressReviewFlag flag={address.reviewFlag} />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={style.actions}>
                        {isRemoved ? (
                          <IconAction
                            label="Undo Remove"
                            renderIcon={Undo}
                            tooltipAlignment="end"
                            onClick={() => handleRestore(address)}
                          />
                        ) : (
                          <>
                            {address.reviewFlag && (
                              <IconAction
                                label="Clear Flag"
                                renderIcon={Flag}
                                tooltipAlignment="end"
                                onClick={() => handleClearFlag(address)}
                              />
                            )}
                            <IconAction
                              label={
                                isComplete
                                  ? 'Remove Correct Status'
                                  : 'Mark as Correct'
                              }
                              renderIcon={CheckmarkOutline}
                              tooltipAlignment="end"
                              onClick={() => handleMarkCorrect(address)}
                            />
                            <IconAction
                              label="Edit Address"
                              renderIcon={Edit}
                              blockedReason={correctStatusLockReason(
                                isComplete,
                                'edit'
                              )}
                              tooltipAlignment="end"
                              onClick={() => setEditTarget(address)}
                            />
                            <IconAction
                              label="Resolve to Party"
                              renderIcon={UserFollow}
                              blockedReason={
                                parties.length === 0
                                  ? 'No parties available'
                                  : correctStatusLockReason(
                                      isComplete,
                                      'resolve'
                                    )
                              }
                              tooltipAlignment="end"
                              onClick={() => setResolveTarget(address)}
                            />
                            <IconAction
                              label="Remove Address"
                              renderIcon={TrashCan}
                              blockedReason={correctStatusLockReason(
                                isComplete,
                                'remove'
                              )}
                              tooltipAlignment="end"
                              onClick={() => handleRemove(address)}
                            />
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddDocumentAddressPanel
        open={addOpen}
        mode="add"
        onClose={() => setAddOpen(false)}
        onSubmit={submitCreate}
      />

      <AddDocumentAddressPanel
        open={!!editTarget}
        mode="edit"
        initial={
          editTarget
            ? {
                street_address: editTarget.streetAddress ?? '',
                city: editTarget.city ?? '',
                state: editTarget.state ?? '',
                zip_code: editTarget.zipCode ?? '',
              }
            : undefined
        }
        onClose={() => setEditTarget(null)}
        onSubmit={submitEdit}
      />

      <ResolveToPartyPanel
        open={!!resolveTarget}
        address={resolveTarget}
        parties={parties}
        onClose={() => setResolveTarget(null)}
        onSubmit={submitResolve}
      />
    </section>
  );
};
